import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Award, CheckCircle2, XCircle, Layers, Building2, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Page publique de verification d'un parchemin.
 * Accessible sans authentification a /verify/:parchemin_unique_id
 * Equivalent du registre RNCP : permet aux recruteurs de verifier
 * l'authenticite d'un parchemin scanne via QR code.
 */
export default function VerifyParcheminPage() {
  const { parchemin_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/certifications/verify/${parchemin_id}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "Verification impossible"))
      .finally(() => setLoading(false));
  }, [parchemin_id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#14101c] via-[#1a1625] to-[#14101c] flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        <Link to="/" className="text-[#f7c935] text-sm hover:underline mb-4 inline-block">← Retour Blitzz</Link>

        {loading && (
          <div className="bg-[#14101c] border border-white/10 rounded-2xl p-8 text-center" data-testid="verify-loading">
            <div className="text-slate-400">Verification en cours...</div>
          </div>
        )}

        {error && !loading && (
          <div className="bg-[#14101c] border border-red-500/30 rounded-2xl p-8 text-center" data-testid="verify-error">
            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <h1 className="text-2xl font-bold text-white mb-2">Parchemin invalide</h1>
            <p className="text-slate-400">{error}</p>
            <p className="text-xs text-slate-500 mt-4">Identifiant verifie : <span className="font-mono">{parchemin_id}</span></p>
          </div>
        )}

        {data && !loading && !error && (
          <div className="bg-gradient-to-br from-[#14101c] to-[#1a1625] border-2 border-[#f7c935]/30 rounded-2xl overflow-hidden shadow-2xl shadow-[#f7c935]/10" data-testid="verify-success">
            <div className="bg-[#f7c935]/5 border-b border-[#f7c935]/20 p-6 text-center">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-3 text-emerald-400" />
              <h1 className="text-3xl font-bold text-white mb-1">Parchemin authentique</h1>
              <p className="text-sm text-slate-400">Verification effectuee · Identifiant <span className="font-mono text-[#f7c935]">{data.parchemin_unique_id}</span></p>
            </div>

            <div className="p-6 lg:p-8 space-y-6">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Titulaire</div>
                <h2 className="text-2xl font-bold text-white" data-testid="candidate-name">{data.candidate_name}</h2>
              </div>

              <div className="bg-[#1a1625] rounded-xl border border-white/5 p-5">
                <div className="flex items-start gap-3">
                  <Award className="w-6 h-6 text-[#f7c935] flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs px-2 py-1 rounded-md bg-purple-500/10 text-purple-400 border border-purple-500/30 font-mono font-semibold">{data.certification.type}</span>
                      <span className="text-xs text-[#f7c935] font-mono">{data.certification.code}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-1" data-testid="certification-title">{data.certification.title}</h3>
                    {data.certification.niveau && <p className="text-sm text-slate-400">{data.certification.niveau}</p>}
                    {data.certification.france_competences_url && (
                      <a href={data.certification.france_competences_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#f7c935] hover:underline mt-2">
                        Fiche France Competences <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-[#1a1625] rounded-xl border border-white/5 p-4">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5"><Layers className="w-3 h-3" /> Blocs valides</div>
                  <div className="text-2xl font-bold text-emerald-400">{data.blocs_valides_count} <span className="text-base font-normal text-slate-500">/ {data.blocs_total_count}</span></div>
                </div>
                <div className="bg-[#1a1625] rounded-xl border border-white/5 p-4">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Statut</div>
                  <div className="text-base font-semibold text-white capitalize">{data.cert_status === "certified" ? "Certifie" : "Validation partielle"}</div>
                </div>
                <div className="bg-[#1a1625] rounded-xl border border-white/5 p-4">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Date d'edition</div>
                  <div className="text-base font-semibold text-white">{(data.issued_at || "").slice(0, 10)}</div>
                </div>
              </div>

              <div className="border-t border-white/5 pt-5">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Delivre par</div>
                <div className="text-lg font-semibold text-white" data-testid="certificateur-name">{data.certificateur.name}</div>
                <p className="text-xs text-slate-500 mt-1">Certificateur enregistre a France Competences</p>
              </div>
            </div>
          </div>
        )}

        <div className="text-center mt-6 text-xs text-slate-500">
          Verification publique · <a href="https://blitzz.fr" className="text-[#f7c935] hover:underline">Blitzz</a>
        </div>
      </div>
    </div>
  );
}
